import { nullable, z } from "zod";
import { SpecificRule } from "../../../shared/types/rate/SpecificRule";
import { SpecificAmount } from "../../../shared/types/rate/SpecificAmountType";

const Children = z.object({
    id: z.string(),
    name: z.string(),
    min_night: z.coerce.number(),
    max_night: z.coerce.number(),
    guest_include: z.coerce.number(),
    guest_max: z.coerce.number(),
    extra_adult: z.coerce.number(),
    extra_child: z.coerce.number(),
    extra_adult_rate: z.coerce.number(),
    extra_child_rate: z.coerce.number(),
    specific_rule: z.enum(SpecificRule.map((s) => s.value) as [string, ...string[]]),
    specific_amount_type: z.enum(SpecificAmount.map((s) => s.value) as [string, ...string[]]),
    specific_value: z.coerce.number(),
    parent_id: z.string(),
});

const RoomType = z.object({
    id: z.string(),
    property_id: z.string(),
    // Nullable: property deactivation nulls this server-side (DeactivatePropertyChannexJob).
    channex_id: z.string().nullable(),
    name: z.string(),
    description: z.nullable(z.string()).optional(),
    no_of_beds: z.number(),
    cut_of_days: z.number(),
    is_free_allotment: z.boolean(),
    is_auto_assign: z.boolean(),
    is_dynamic_pricing: z.boolean(),
    total_rooms: z.number(),
    created_at: z.string(),
    updated_at: z.string(),
    occ_adults: z.number(),
    occ_children: z.number(),
    occ_infants: z.number(),
    default_occupancy: z.number(),
    room_kind: z.string(),
    capacity: z.number(),
});

const DataSchema = z.object({
    id: z.string(),
    property_id: z.string(),
    // Nullable: property deactivation nulls this server-side (DeactivatePropertyChannexJob).
    channex_id: z.string().nullable(),
    room_type_id: z.string(),
    parent_id: z.nullable(z.string()),
    name: z.string(),
    description: z.nullable(z.string()).optional(),
    min_rate: z.coerce.number(),
    parent_rate: z.nullable(z.coerce.number()),
    rate: z.coerce.number(),
    min_night: z.number(),
    max_night: z.number(),
    guest_include: z.number(),
    guest_max: z.number(),
    extra_adult: z.number(),
    extra_child: z.number(),
    extra_adult_rate: nullable(z.coerce.number()),
    extra_child_rate: nullable(z.coerce.number()),
    inherit_restriction: z.number(),
    inherit_closed_to_arrival: z.number(),
    inherit_closed_to_departure: z.number(),
    inherit_stop_sell: z.number(),
    inherit_min_stay_arrival: z.number(),
    inherit_min_stay_through: z.number(),
    inherit_max_stay: z.number(),
    type: z.enum(['link', 'rate']),
    specific_rule: z.nullable(z.enum(SpecificRule.map((s) => s.value) as [string, ...string[]])),
    specific_amount_type: z.nullable(z.enum(SpecificAmount.map((s) => s.value) as [string, ...string[]])),
    specific_value: z.coerce.number(),
    created_at: z.string(),
    updated_at: z.string(),
    room_type: RoomType.optional(),
    children: Children.optional(),
});

const ShowRatePlanSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: DataSchema,
});

export { ShowRatePlanSchema };
export type RatePlanResponse = z.infer<typeof DataSchema>;
export type ShowRatePlanResponse = z.infer<typeof ShowRatePlanSchema>;